import React, { useState, useEffect, useRef } from 'react';
import Icon from '../AppIcon';

const MultiSelectAutocomplete = ({
  selectedValues = [],
  onChange,
  options = [],
  placeholder = "Escribir para buscar...",
  displayKey = "name",
  valueKey = "id",
  className = "",
  required = false,
  maxSelections = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Filter options based on input and exclude already selected
  useEffect(() => {
    let filtered = options.filter(option => 
      !selectedValues.includes(option[valueKey])
    );

    if (inputValue.trim()) {
      filtered = filtered.filter(option =>
        option[displayKey].toLowerCase().includes(inputValue.toLowerCase())
      );
    }

    setFilteredOptions(filtered);
  }, [inputValue, options, selectedValues, displayKey, valueKey]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setIsOpen(true);
  };

  const handleOptionSelect = (option) => {
    if (maxSelections && selectedValues.length >= maxSelections) {
      return;
    }

    const newSelectedValues = [...selectedValues, option[valueKey]];
    onChange(newSelectedValues);
    setInputValue('');
    setIsOpen(false);
  };

  const handleRemoveSelection = (valueToRemove) => {
    const newSelectedValues = selectedValues.filter(val => val !== valueToRemove);
    onChange(newSelectedValues);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredOptions.length === 1) {
        handleOptionSelect(filteredOptions[0]);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Backspace' && !inputValue && selectedValues.length > 0) {
      // Remove last selected item when backspace is pressed on empty input
      handleRemoveSelection(selectedValues[selectedValues.length - 1]);
    }
  };

  const getSelectedOptionName = (value) => {
    const option = options.find(opt => opt[valueKey] === value);
    return option ? option[displayKey] : value;
  };

  return (
    <div className={className} ref={dropdownRef}>
      {/* Selected items */}
      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedValues.map((value) => (
            <span
              key={value}
              className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs"
            >
              {getSelectedOptionName(value)}
              <button
                type="button"
                onClick={() => handleRemoveSelection(value)}
                className="text-primary hover:text-primary/80 ml-1"
              >
                <Icon name="X" size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedValues.length > 0 ? "Agregar más..." : placeholder}
          required={required && selectedValues.length === 0}
          disabled={maxSelections && selectedValues.length >= maxSelections}
          className="w-full px-3 py-2 pr-8 border border-slate-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary text-sm text-slate-800 disabled:bg-slate-50 disabled:text-slate-500"
        />
        <Icon 
          name={isOpen ? "ChevronUp" : "ChevronDown"} 
          size={16} 
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none"
        />
      </div>

      {/* Dropdown */}
      {isOpen && !maxSelections || (maxSelections && selectedValues.length < maxSelections) ? (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <button
                key={option[valueKey]}
                type="button"
                onClick={() => handleOptionSelect(option)}
                className="w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 focus:bg-slate-50 focus:outline-none"
              >
                {option[displayKey]}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-slate-500">
              {inputValue ? 'No se encontraron resultados' : 'Todas las opciones ya están seleccionadas'}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default MultiSelectAutocomplete;

